// =============================================================================
// VandieZ admin-web -- Zones master (/masterdata/zones)
// =============================================================================
// CRUD over the `pricing_zones` / `zone_members` tables (WS-D/D1 Task 7).
// Mirrors LocationsPage.tsx's shape (Task 6): same PageHeader / card layout,
// same friendly-error translation pattern, same "PATCH {is_active}"
// soft-toggle instead of a hard delete.
//
// Two things this page adds beyond Locations:
//   - An optional GeoJSON geometry pasted into a Textarea on create.
//     `JSON.parse` runs client-side first -- a parse failure never reaches
//     the network. A syntactically-valid-but-not-a-polygon body still goes
//     to the backend, which can 422 with INVALID_ZONE_GEOMETRY; that too is
//     translated to a friendly message.
//   - A member manager: the backend has no "list members of a zone"
//     endpoint, only add/remove-by-location-id and a `member_count` on the
//     zone row -- so the manager is a single location picker (populated
//     from `listLocations`) with Add / Remove buttons, per the brief.
// =============================================================================

import { AlertCircle, Loader2, Plus, Users } from "lucide-react";
import type React from "react";
import { Fragment, useCallback, useEffect, useState } from "react";

import { masterdataAdminApi } from "@/api/client";
import { PageHeader } from "@/components/AdminLayout";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/lib/auth";
import { ApiError } from "@/types/auth";
import type { Location, Zone, ZoneCreatePayload } from "@/types/masterdata";

/// Translate a caught error into the message shown in the page-level /
/// form-level Alert. The three backend error codes this page can hit get
/// friendlier renderings than the raw detail string.
function friendlyError(e: unknown): string {
  if (e instanceof ApiError) {
    if (e.code === "ZONE_CODE_DUPLICATE") {
      return "A zone with that code already exists.";
    }
    if (e.code === "INVALID_ZONE_GEOMETRY") {
      return "The zone geometry is not valid GeoJSON polygon data.";
    }
    if (e.code === "LOCATION_ALREADY_ZONED") {
      return "That location already belongs to a zone. Remove it there first.";
    }
    return e.problem.detail;
  }
  return "Unexpected error.";
}

interface CreateZoneFormProps {
  onCreated: () => void;
  onCancel: () => void;
}

function CreateZoneForm({
  onCreated,
  onCancel,
}: CreateZoneFormProps): React.JSX.Element {
  const { accessToken } = useAuth();
  const [code, setCode] = useState<string>("");
  const [name, setName] = useState<string>("");
  const [geometryText, setGeometryText] = useState<string>("");
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    if (accessToken === null) {
      return;
    }
    if (code.trim().length === 0 || name.trim().length === 0) {
      setError("Code and name are required.");
      return;
    }

    let geometry: Record<string, unknown> | undefined;
    if (geometryText.trim().length > 0) {
      try {
        geometry = JSON.parse(geometryText) as Record<string, unknown>;
      } catch {
        setError("The geometry field must be valid JSON.");
        return;
      }
    }

    setSubmitting(true);
    setError(null);
    try {
      const payload: ZoneCreatePayload = {
        code: code.trim(),
        name: name.trim(),
      };
      if (geometry !== undefined) {
        payload.geometry = geometry;
      }
      await masterdataAdminApi.createZone(accessToken, payload);
      onCreated();
    } catch (e: unknown) {
      setError(friendlyError(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>New zone</CardTitle>
        <CardDescription>
          Code + name are required and the code must be unique. Geometry is
          optional GeoJSON pasted as raw JSON -- it must be a single Polygon
          (multipolygon is not supported); leave it blank to create a zone
          without a shape yet.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid gap-3 md:grid-cols-2">
          <div className="space-y-1">
            <Label htmlFor="zone-code">Code</Label>
            <Input
              id="zone-code"
              value={code}
              onChange={(e) => {
                setCode(e.target.value);
              }}
              placeholder="CHN-METRO"
              disabled={submitting}
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="zone-name">Name</Label>
            <Input
              id="zone-name"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
              }}
              placeholder="Chennai Metro"
              disabled={submitting}
            />
          </div>
        </div>

        <div className="space-y-1">
          <Label htmlFor="zone-geometry">Geometry (optional GeoJSON)</Label>
          <Textarea
            id="zone-geometry"
            value={geometryText}
            onChange={(e) => {
              setGeometryText(e.target.value);
            }}
            placeholder='{"type":"Polygon","coordinates":[[[80.20,12.97],...]]}'
            disabled={submitting}
            rows={4}
          />
        </div>

        {error !== null && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onCancel} disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={() => void submit()} disabled={submitting}>
            {submitting ? (
              <>
                <Loader2 className="mr-1 h-4 w-4 animate-spin" /> Creating…
              </>
            ) : (
              "Create zone"
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

interface ZoneMemberPanelProps {
  zone: Zone;
  locations: Location[];
  onChanged: () => void;
}

/// Add / remove a single location from a zone. The backend has no
/// "list current members" endpoint -- only `member_count` on the zone row
/// -- so this panel is deliberately a single picker with Add + Remove,
/// not a two-column mover.
function ZoneMemberPanel({
  zone,
  locations,
  onChanged,
}: ZoneMemberPanelProps): React.JSX.Element {
  const { accessToken } = useAuth();
  const [locationId, setLocationId] = useState<string>(
    locations[0]?.id ?? "",
  );
  const [busy, setBusy] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const add = async () => {
    if (accessToken === null || locationId.length === 0) {
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await masterdataAdminApi.addZoneMember(accessToken, zone.id, locationId);
      onChanged();
    } catch (e: unknown) {
      setError(friendlyError(e));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (accessToken === null || locationId.length === 0) {
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await masterdataAdminApi.removeZoneMember(accessToken, zone.id, locationId);
      onChanged();
    } catch (e: unknown) {
      setError(friendlyError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-2 rounded-md border bg-muted/30 p-3">
      <div className="flex flex-wrap items-end gap-3">
        <div className="min-w-[220px] flex-1 space-y-1">
          <Label htmlFor={`zone-member-location-${zone.id}`}>Location</Label>
          <select
            id={`zone-member-location-${zone.id}`}
            className="block w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            value={locationId}
            onChange={(e) => {
              setLocationId(e.target.value);
            }}
            disabled={busy || locations.length === 0}
          >
            {locations.length === 0 && <option value="">No locations available</option>}
            {locations.map((loc) => (
              <option key={loc.id} value={loc.id}>
                {loc.city}, {loc.district} ({loc.state})
              </option>
            ))}
          </select>
        </div>
        <Button
          size="sm"
          onClick={() => void add()}
          disabled={busy || locationId.length === 0}
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Add"}
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={() => void remove()}
          disabled={busy || locationId.length === 0}
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Remove"}
        </Button>
      </div>
      <p className="text-xs text-muted-foreground">
        Currently {zone.member_count} location{zone.member_count === 1 ? "" : "s"}{" "}
        in this zone. There is no member listing endpoint yet -- add or
        remove by picking the location above.
      </p>
      {error !== null && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
    </div>
  );
}

export function ZonesPage(): React.JSX.Element {
  const { accessToken } = useAuth();
  const [zones, setZones] = useState<Zone[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState<boolean>(false);
  const [togglingId, setTogglingId] = useState<string | null>(null);
  const [managingZoneId, setManagingZoneId] = useState<string | null>(null);

  const load = useCallback(
    async (signal?: AbortSignal) => {
      if (accessToken === null) {
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const res = await masterdataAdminApi.listZones(accessToken, signal);
        setZones(res);
      } catch (e: unknown) {
        if (e instanceof Error && e.name === "AbortError") {
          return;
        }
        setError(friendlyError(e));
      } finally {
        setLoading(false);
      }
    },
    [accessToken],
  );

  useEffect(() => {
    const controller = new AbortController();
    void load(controller.signal);
    return () => {
      controller.abort();
    };
  }, [load]);

  // Locations back the member-add picker. Fetched once, independent of the
  // zones list's own filters (Locations has none today, so this is simply
  // "all locations").
  useEffect(() => {
    if (accessToken === null) {
      return;
    }
    const controller = new AbortController();
    masterdataAdminApi
      .listLocations(accessToken, {}, controller.signal)
      .then(setLocations)
      .catch(() => {
        // Non-fatal: the member picker just renders empty. The zones list
        // above already reports its own errors.
      });
    return () => {
      controller.abort();
    };
  }, [accessToken]);

  const toggleActive = async (zone: Zone) => {
    if (accessToken === null) {
      return;
    }
    setTogglingId(zone.id);
    try {
      await masterdataAdminApi.updateZone(accessToken, zone.id, {
        is_active: !zone.is_active,
      });
      await load();
    } catch (e: unknown) {
      setError(friendlyError(e));
    } finally {
      setTogglingId(null);
    }
  };

  return (
    <div className="space-y-4">
      <PageHeader
        title="Zones"
        description="Pricing / serviceability zones grouping locations for zone-based pricing (WS-D slice D1)."
        actions={
          !creating && (
            <Button
              onClick={() => {
                setCreating(true);
              }}
            >
              <Plus className="mr-1 h-4 w-4" /> New zone
            </Button>
          )
        }
      />

      {error !== null && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {creating && (
        <CreateZoneForm
          onCreated={() => {
            setCreating(false);
            void load();
          }}
          onCancel={() => {
            setCreating(false);
          }}
        />
      )}

      <Card>
        <CardHeader>
          <CardTitle>Zones ({zones.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {loading && zones.length === 0 ? (
            <div className="flex items-center gap-2 py-8 text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading…
            </div>
          ) : zones.length === 0 ? (
            <div className="py-10 text-center text-muted-foreground">
              No zones match the current filters.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <tr>
                    <th className="py-2 pr-4">Code</th>
                    <th className="py-2 pr-4">Name</th>
                    <th className="py-2 pr-4">Geometry</th>
                    <th className="py-2 pr-4">Members</th>
                    <th className="py-2 pr-4">Status</th>
                    <th className="py-2 pr-4"></th>
                  </tr>
                </thead>
                <tbody>
                  {zones.map((zone) => (
                    <Fragment key={zone.id}>
                      <tr className="border-b align-top last:border-b-0">
                        <td className="py-3 pr-4 font-medium">{zone.code}</td>
                        <td className="py-3 pr-4">{zone.name}</td>
                        <td className="py-3 pr-4 text-xs text-muted-foreground">
                          {zone.has_geometry ? "Yes" : "No"}
                        </td>
                        <td className="py-3 pr-4 tabular-nums">{zone.member_count}</td>
                        <td className="py-3 pr-4">
                          <span
                            className={
                              zone.is_active
                                ? "inline-flex items-center rounded-full bg-success-subtle px-2 py-0.5 text-xs font-semibold text-success-subtle-foreground"
                                : "inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-xs font-semibold text-muted-foreground"
                            }
                          >
                            {zone.is_active ? "Active" : "Inactive"}
                          </span>
                        </td>
                        <td className="py-3 pr-4">
                          <div className="flex gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setManagingZoneId(
                                  managingZoneId === zone.id ? null : zone.id,
                                );
                              }}
                            >
                              <Users className="mr-1 h-4 w-4" /> Manage members
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              disabled={togglingId === zone.id}
                              onClick={() => void toggleActive(zone)}
                            >
                              {togglingId === zone.id ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : zone.is_active ? (
                                "Deactivate"
                              ) : (
                                "Activate"
                              )}
                            </Button>
                          </div>
                        </td>
                      </tr>
                      {managingZoneId === zone.id && (
                        <tr className="border-b last:border-b-0">
                          <td colSpan={6} className="py-3 pr-4">
                            <ZoneMemberPanel
                              zone={zone}
                              locations={locations}
                              onChanged={() => {
                                void load();
                              }}
                            />
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
